import { ArrowLeft, Save, Trash2, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { cn } from '@/lib/utils'
import type { CaseData } from './types'

interface CaseHeaderProps {
  caseData: CaseData
  hasChanges: boolean
  isSaving: boolean
  isDeleting: boolean
  onSave: () => void
  onDelete: () => void
  onBack: () => void
}

export function CaseHeader({
  caseData,
  hasChanges,
  isSaving,
  isDeleting,
  onSave,
  onDelete,
  onBack,
}: CaseHeaderProps) {
  return (
    <div className="flex items-center justify-between mb-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={onBack}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-slate-900 font-mono tracking-wide">
              {caseData.token}
            </h1>
            <Badge
            className={cn(
              'font-medium',
              caseData.is_draft
                ? 'bg-slate-100 text-slate-600'
                : 'bg-highlight text-highlight-text border-highlight'
            )}
          >
            {caseData.state_display}
          </Badge>
          </div>
          <p className="text-sm text-slate-600 font-medium">
            {caseData.summary_title}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button onClick={onSave} disabled={!hasChanges || isSaving} className="gap-2">
          {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Speichern
        </Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="destructive" className="gap-2">
              <Trash2 className="h-4 w-4" />
              Löschen
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Fall {caseData.token} löschen?</AlertDialogTitle>
              <AlertDialogDescription>
                Diese Aktion kann nicht rückgängig gemacht werden. Der Fall und alle zugehörigen
                Daten (Passagiere, Flugsegmente, Dokumente, Ausgaben) werden dauerhaft gelöscht.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
              <AlertDialogAction
                onClick={onDelete}
                disabled={isDeleting}
                className="bg-red-600 hover:bg-red-700"
              >
                {isDeleting ? 'Wird gelöscht...' : 'Fall löschen'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  )
}
